import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Fade,
  Backdrop,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  IconButton,
  Autocomplete,
  Grid,
  alpha,
  useTheme,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import AddIcon from "@mui/icons-material/Add";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import NorthWestOutlinedIcon from "@mui/icons-material/NorthWestOutlined";
import SouthEastOutlinedIcon from "@mui/icons-material/SouthEastOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTranslation } from "react-i18next";
import { readTabUserData } from "../../../utils/tabAuthStorage";
import { useNavigate, useLocation } from "react-router-dom";
import { useUsers } from "../../../hooks/useUsers";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import TaskCardChecklists from "./TaskCardChecklists";
import TaskCardAttachments from "./TaskCardAttachments";
import TaskCardComments from "./TaskCardComments";
import CreateLinkedTaskDialog from "./CreateLinkedTaskDialog";
import { useTeamBoardAction } from "../hooks/useTeamBoardAction";
import { printTaskCard } from "../utils/printTaskCard";
import { useEnrichedTaskComments } from "../hooks/useEnrichedTaskComments";
import { useBrand } from "../../../context/BrandContext";
import FileDropHandler from "../../../components/MessageHistory/FileDropHandler";
import { TASK_COMMENT_DROP_ZONE_SELECTOR } from "./TaskCommentComposer";
import TaskColumnActions, { pickDoneColumn, pickReopenColumn } from "./TaskColumnActions";
import {
  teamTasksPaths,
  resolveTeamBaseFromPathname,
  resolveServiceChatNavPath,
  chatPath,
} from "../../../utils/teamNavigation";
import { buildBoardAssigneePickerOptions } from "../utils/teamBoardCardFilters";
import TaskStageTimeBar from "./TaskStageTimeBar";
import { formatTaskDisplayName } from "../utils/taskDisplayName";

const EMPTY_BOARD_MEMBERS = [];

const QuickPill = ({ icon, label, active, onClick, disabled }) => (
  <Chip
    icon={icon}
    label={label}
    size="small"
    clickable={!disabled}
    onClick={disabled ? undefined : onClick}
    variant={active ? "filled" : "outlined"}
    color={active ? "primary" : "default"}
    sx={{ fontWeight: 600, borderRadius: 2, height: 32 }}
  />
);

const LinkedTaskRow = ({ link, role, onOpen, t, theme }) => {
  const isDone = Boolean(link?.done || link?.completedAt || link?.columnIsDone);
  const roleLabel = role === "parent" ? t("linkedTaskParent") : t("linkedTaskChildren");
  const RoleIcon = role === "parent" ? NorthWestOutlinedIcon : SouthEastOutlinedIcon;

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(link)}
      aria-label={`${t("linkedTaskOpenHint")}: ${formatTaskDisplayName(link.id, link.title)}`}
      sx={{
        all: "unset",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        width: "100%",
        px: 1.25,
        py: 1,
        cursor: "pointer",
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
        bgcolor: alpha(theme.palette.background.default, 0.55),
        transition: "border-color 0.15s ease, background-color 0.15s ease, transform 0.15s ease",
        "&:hover": {
          borderColor: alpha(theme.palette.primary.main, 0.45),
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        },
        "&:active": { transform: "scale(0.995)" },
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.5,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(
            isDone ? theme.palette.success.main : theme.palette.primary.main,
            0.12
          ),
          color: isDone ? "success.main" : "primary.main",
        }}
      >
        <RoleIcon sx={{ fontSize: 18 }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "text.secondary",
              letterSpacing: 0.2,
              textTransform: "none",
            }}
          >
            {roleLabel}
          </Typography>
          {link.boardName ? (
            <Chip
              size="small"
              label={link.boardName}
              sx={{
                height: 20,
                fontSize: "0.68rem",
                fontWeight: 600,
                bgcolor: alpha(theme.palette.action.active, 0.06),
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          ) : null}
          {isDone ? (
            <Chip
              size="small"
              icon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
              label={t("linkedTaskDone")}
              sx={{
                height: 20,
                fontSize: "0.68rem",
                fontWeight: 700,
                bgcolor: alpha(theme.palette.success.main, 0.12),
                color: "success.main",
                "& .MuiChip-icon": { color: "inherit", ml: 0.5 },
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          ) : link.columnName ? (
            <Typography variant="caption" color="text.secondary" noWrap>
              {link.columnName}
            </Typography>
          ) : null}
        </Stack>
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          sx={{
            mt: 0.2,
            color: "text.primary",
            letterSpacing: "-0.01em",
          }}
        >
          {formatTaskDisplayName(link.id, link.title)}
        </Typography>
      </Box>

      <ChevronRightIcon sx={{ fontSize: 20, color: "text.disabled", flexShrink: 0 }} />
    </Box>
  );
};

const TaskCardModal = ({
  open,
  card,
  board,
  onClose,
  onSave,
  onDelete,
  onMoveColumn,
  canManage,
  canExecute = false,
  cardMutations,
  onSelectCard,
}) => {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const brand = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const { run } = useTeamBoardAction();
  const { data: usersData } = useUsers();
  const users = usersData?.users || usersData || [];
  const tasksBasePath = resolveTeamBaseFromPathname(location.pathname);
  const tasksPaths = teamTasksPaths(tasksBasePath);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [estimatedDuration, setEstimatedDuration] = useState("");
  const [isRequired, setIsRequired] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [clientAssignees, setClientAssignees] = useState([]);
  const [assignedToWorkspace, setAssignedToWorkspace] = useState(false);
  const [labelIds, setLabelIds] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDueField, setShowDueField] = useState(false);
  const [showDurationField, setShowDurationField] = useState(false);
  const [showAssignees, setShowAssignees] = useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [addingChecklist, setAddingChecklist] = useState(false);
  const [movingColumn, setMovingColumn] = useState(false);
  const [linkedDialogOpen, setLinkedDialogOpen] = useState(false);
  const [creatingLinked, setCreatingLinked] = useState(false);
  const attachmentsRef = useRef(null);

  const boardLabels = board?.labels || [];
  const boardId = board?.id;
  const boardColumns = board?.columns || [];
  // Stable empty fallback — `|| []` would create a new array every render and
  // re-trigger assignee hydration (wiping in-progress edits).
  const boardMembers = board?.members || EMPTY_BOARD_MEMBERS;
  const canMove = Boolean(canExecute || canManage) && Boolean(onMoveColumn);
  const inDoneColumn = useMemo(() => {
    const col = boardColumns.find((c) => c.id === card?.columnId);
    return Boolean(col?.isDone);
  }, [boardColumns, card?.columnId]);

  const assigneeOptions = useMemo(
    () =>
      buildBoardAssigneePickerOptions(boardMembers, users, {
        includeIds: card?.assignees || [],
      }),
    [boardMembers, users, card?.assignees]
  );

  const currentUserId = useMemo(() => {
    try {
      return readTabUserData()?.id ?? null;
    } catch {
      return null;
    }
  }, []);

  const isClientBoard = Boolean(board?.clientId != null && Number(board.clientId) > 0);
  const linkedPfClients = useMemo(
    () =>
      (board?.linkedPfClients || []).map((c) => ({
        id: Number(c.id),
        name: c.name || `Cliente #${c.id}`,
        tipo: c.tipo ?? 0,
      })),
    [board?.linkedPfClients]
  );
  // No painel do cliente, quem pode executar também edita (atribuir, prazo, etc.).
  const canEdit =
    Boolean(canManage) ||
    (isClientBoard && Boolean(canExecute)) ||
    (Number(currentUserId) > 0 && Number(card?.createdBy) === Number(currentUserId));

  // Reset form fields only when the open card changes — not when users/members hydrate.
  useEffect(() => {
    if (!card) return;
    setTitle(card.title || "");
    setDescription(card.description || "");
    setDueAt(card.dueAt ? String(card.dueAt).slice(0, 16).replace(" ", "T") : "");
    setEstimatedDuration(
      card.estimatedDuration ? String(card.estimatedDuration).slice(0, 5) : ""
    );
    setIsRequired(Boolean(card.isRequired));
    setShowDueField(Boolean(card.dueAt));
    setShowDurationField(Boolean(card.estimatedDuration));
    const hasUserAssignees = (card.assignees || []).length > 0;
    const hasClientAssignees = (card.clientAssignees || []).length > 0;
    const hasWorkspace = Boolean(card.assignedToWorkspace);
    setShowAssignees(hasUserAssignees || hasClientAssignees || hasWorkspace);
    setAssignedToWorkspace(hasWorkspace);
    setClientAssignees(
      (card.clientAssignees || []).map((c) => ({
        id: Number(c.id),
        name: c.name || `Cliente #${c.id}`,
        tipo: c.tipo ?? 0,
      }))
    );
    setLabelIds((card.labels || []).map((l) => Number(l.id)));
  }, [card]);

  // Resolve assignee chips when the picker option list becomes available/updates.
  // Do not clobber in-progress edits that already diverge from the saved card.
  useEffect(() => {
    if (!card) return;
    const cardIds = (card.assignees || [])
      .map(Number)
      .filter((id) => Number.isFinite(id) && id > 0);
    const cardIdSet = new Set(cardIds);
    const sortedKey = (ids) =>
      [...ids].map(Number).filter((id) => Number.isFinite(id)).sort((a, b) => a - b).join(",");

    setAssignees((prev) => {
      const prevKey = sortedKey(prev.map((u) => u.id));
      const cardKey = sortedKey(cardIds);
      if (prev.length > 0 && prevKey !== cardKey) {
        return prev.map(
          (u) => assigneeOptions.find((o) => Number(o.id) === Number(u.id)) || u
        );
      }
      const next = assigneeOptions.filter((u) => cardIdSet.has(Number(u.id)));
      return sortedKey(next.map((u) => u.id)) === prevKey ? prev : next;
    });
  }, [card, assigneeOptions]);

  const hasChanges = useMemo(() => {
    if (!card) return false;

    const normalizeDueAt = (value) =>
      value ? String(value).slice(0, 16).replace("T", " ") : null;
    const normalizeDuration = (value) =>
      value ? String(value).slice(0, 5) : null;
    const sortedIds = (ids) =>
      [...ids].map(Number).filter((id) => Number.isFinite(id)).sort((a, b) => a - b);

    const nextTitle = title.trim();
    const nextDescription = description.trim() || null;
    const nextDueAt = normalizeDueAt(dueAt);
    const nextEstimatedDuration = normalizeDuration(estimatedDuration);
    const nextAssignees = sortedIds(assignees.map((u) => u.id));
    const nextClientAssignees = sortedIds(clientAssignees.map((c) => c.id));
    const nextLabelIds = sortedIds(labelIds);

    const prevTitle = (card.title || "").trim();
    const prevDescription = (card.description || "").trim() || null;
    const prevDueAt = normalizeDueAt(card.dueAt);
    const prevEstimatedDuration = normalizeDuration(card.estimatedDuration);
    const prevAssignees = sortedIds(card.assignees || []);
    const prevClientAssignees = sortedIds((card.clientAssignees || []).map((c) => c.id));
    const prevLabelIds = sortedIds((card.labels || []).map((l) => l.id));

    // Avoid false dirty state while assignee options are still hydrating.
    const canCompareAssignees =
      prevAssignees.length === 0 || assigneeOptions.length > 0;
    const assigneesChanged =
      canCompareAssignees && nextAssignees.join(",") !== prevAssignees.join(",");
    const clientAssigneesChanged =
      nextClientAssignees.join(",") !== prevClientAssignees.join(",");
    const workspaceChanged =
      Boolean(assignedToWorkspace) !== Boolean(card.assignedToWorkspace);

    return (
      nextTitle !== prevTitle ||
      nextDescription !== prevDescription ||
      nextDueAt !== prevDueAt ||
      nextEstimatedDuration !== prevEstimatedDuration ||
      Boolean(isRequired) !== Boolean(card.isRequired) ||
      assigneesChanged ||
      clientAssigneesChanged ||
      workspaceChanged ||
      nextLabelIds.join(",") !== prevLabelIds.join(",")
    );
  }, [
    card,
    title,
    description,
    dueAt,
    estimatedDuration,
    isRequired,
    assignees,
    clientAssignees,
    assignedToWorkspace,
    labelIds,
    assigneeOptions,
  ]);

  const discussionSpaceId =
    card?.discussionSpaceId != null && Number(card.discussionSpaceId) > 0
      ? Number(card.discussionSpaceId)
      : null;
  const enrichedComments = useEnrichedTaskComments(card?.comments || [], discussionSpaceId);

  if (!card) return null;

  const leadId = card.leadId || board?.leadId;
  const showCardSidebar = Boolean(leadId || discussionSpaceId);

  const handleMoveColumn = async (columnId) => {
    if (!onMoveColumn || movingColumn) return;
    setMovingColumn(true);
    try {
      await onMoveColumn(columnId);
    } finally {
      setMovingColumn(false);
    }
  };

  const handleQuickComplete = async () => {
    const doneCol = pickDoneColumn(boardColumns);
    if (doneCol) await handleMoveColumn(doneCol.id);
  };

  const handleQuickReopen = async () => {
    const reopenCol = pickReopenColumn(boardColumns, card.columnId);
    if (reopenCol) await handleMoveColumn(reopenCol.id);
  };

  const toggleLabel = (id) => {
    if (!canEdit) return;
    setLabelIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ok = await onSave({
        cardId: card.id,
        title: title.trim(),
        description: description.trim() || null,
        dueAt: dueAt ? dueAt.replace("T", " ") : null,
        estimatedDuration: estimatedDuration || null,
        isRequired,
        assignees: assignees.map((u) => Number(u.id)),
        clientAssignees: isClientBoard
          ? clientAssignees.map((c) => Number(c.id))
          : undefined,
        assignedToWorkspace: isClientBoard ? Boolean(assignedToWorkspace) : undefined,
        labelIds,
      });
      return ok !== false;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const ok = await onDelete(card.id);
      if (ok !== false) setDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleAddChecklist = async () => {
    setAddMenuAnchor(null);
    if (!cardMutations?.createChecklist || !boardId || addingChecklist) return;
    setAddingChecklist(true);
    try {
      await run(
        () =>
          cardMutations.createChecklist.mutateAsync({
            cardId: card.id,
            title: t("checklistDefaultTitle"),
          }),
        { context: "checklist" }
      );
    } finally {
      setAddingChecklist(false);
    }
  };

  const handleAddAttachmentClick = () => {
    attachmentsRef.current?.openFilePicker();
    setAddMenuAnchor(null);
  };

  const handleOpenLinkedDialog = () => {
    setAddMenuAnchor(null);
    setLinkedDialogOpen(true);
  };

  const handleCreateLinkedTask = async (payload) => {
    if (!cardMutations?.createLinkedCard || !boardId || creatingLinked) return false;
    setCreatingLinked(true);
    try {
      const result = await run(
        () =>
          cardMutations.createLinkedCard.mutateAsync({
            cardId: card.id,
            ...payload,
          }),
        { context: "createLinkedCard", successKey: "successLinkedTaskCreated" }
      );
      return result !== null;
    } finally {
      setCreatingLinked(false);
    }
  };

  const handleOpenLinkedCard = (link) => {
    if (!link?.id || !link?.boardId) return;
    if (Number(link.boardId) === Number(boardId) && onSelectCard) {
      onSelectCard({ id: link.id, boardId: link.boardId, columnId: link.columnId });
      return;
    }
    const base = tasksPaths.board(link.boardId);
    navigate(`${base}?card=${link.id}`);
    onClose?.();
  };

  const handleAttachmentDrop = (files) => {
    attachmentsRef.current?.uploadFiles(files);
  };

  const handlePrintTask = () => {
    if (!card) return;
    let printedBy = "";
    try {
      const user = readTabUserData();
      printedBy = user?.nome || user?.name || "";
    } catch {
      printedBy = "";
    }

    const column = (board?.columns || []).find(
      (c) => Number(c.id) === Number(card.columnId)
    );
    const selectedLabels = (board?.labels || []).filter((label) =>
      labelIds.includes(Number(label.id))
    );
    const printAssignees =
      assignees.length > 0
        ? assignees
        : (card.assignees || []).map((id) => {
            const user = (users || []).find((u) => Number(u.id) === Number(id));
            return user || { id, nome: String(id) };
          });

    printTaskCard({
      card: {
        ...card,
        title: title.trim() || card.title,
        description: description.trim() || card.description,
        dueAt: dueAt ? dueAt.replace("T", " ") : card.dueAt,
        estimatedDuration: estimatedDuration || card.estimatedDuration,
        isRequired,
        labels: selectedLabels,
        comments: enrichedComments,
      },
      board,
      column,
      assignees: printAssignees,
      logoUrl: `${window.location.origin}${brand.assets.logo.light}`,
      printedBy,
      copyright: t("print.copyright", {
        year: new Date().getFullYear(),
        brand: brand.name,
      }),
      labels: {
        title: t("print.title"),
        printedAt: t("print.printedAt"),
        printedBy: t("print.printedBy"),
        brandName: brand.name,
        detailsSection: t("print.detailsSection"),
        descriptionSection: t("print.descriptionSection"),
        checklistsSection: t("print.checklistsSection"),
        linkedSection: t("print.linkedSection"),
        attachmentsSection: t("print.attachmentsSection"),
        commentsSection: t("print.commentsSection"),
        systemLog: t("print.systemLog"),
        popupBlocked: t("print.popupBlocked"),
        noDetails: t("print.noDetails"),
        noDescription: t("print.noDescription"),
        noAttachments: t("print.noAttachments"),
        noComments: t("print.noComments"),
        noChecklistItems: t("print.noChecklistItems"),
        board: t("print.fields.board"),
        column: t("print.fields.column"),
        status: t("print.fields.status"),
        dueAt: t("cardDueDate"),
        estimatedDuration: t("cardEstimatedDuration"),
        assignees: t("cardAssignees"),
        required: t("cardRequiredShort"),
        labelsField: t("cardLabels"),
        client: t("cardClientLabel"),
        request: t("linkLead"),
        createdAt: t("print.fields.createdAt"),
        completedAt: t("print.fields.completedAt"),
        yes: t("print.yes"),
        no: t("print.no"),
        done: t("linkedTaskDone"),
        open: t("print.open"),
        linkedParent: t("linkedTaskParent"),
        linkedChild: t("linkedTaskChildren"),
        checklist: t("checklistDefaultTitle"),
        unnamedFile: t("print.unnamedFile"),
        file: t("print.file"),
        edited: t("commentEdited"),
        unknownAuthor: t("commentUnknownAuthor"),
        unknownUser: t("print.unknownUser"),
        imageAlt: t("print.imageAlt"),
        commentImageAlt: t("print.commentImageAlt"),
      },
    });
  };

  const parentLink = card.parent || null;
  const childLinks = Array.isArray(card.children) ? card.children : [];
  const hasLinks = Boolean(parentLink) || childLinks.length > 0;
  const pendingLinkedReview = Boolean(card.linkedCompletionPendingReview);

  const checklistCount = (card.checklists || []).reduce(
    (sum, cl) => sum + (cl.items?.length || 0),
    0
  );
  const attachmentCount = (card.attachments || []).length;
  const commentCount = enrichedComments.length;

  const dueDisplay = dueAt
    ? new Date(dueAt).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 200, sx: { bgcolor: alpha("#000", 0.55) } } }}
      >
        <Fade in={open}>
          <Paper
            elevation={24}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "calc(100% - 24px)", sm: 720, md: 860 },
              maxHeight: "92vh",
              borderRadius: 3,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              outline: "none",
              border: inDoneColumn
                ? `1px solid ${alpha(theme.palette.success.main, 0.35)}`
                : "none",
            }}
          >
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                borderBottom: 1,
                borderColor: inDoneColumn
                  ? alpha(theme.palette.success.main, 0.25)
                  : "divider",
                bgcolor: inDoneColumn
                  ? alpha(theme.palette.success.main, 0.06)
                  : alpha(theme.palette.background.paper, 0.98),
              }}
            >
              {canMove ? (
                <TaskColumnActions
                  boardId={boardId}
                  cardId={card.id}
                  columnId={card.columnId}
                  columnIsDone={inDoneColumn}
                  columns={boardColumns}
                  onMove={handleMoveColumn}
                  disabled={movingColumn}
                  stopPropagation={false}
                />
              ) : null}
              <Box sx={{ flex: 1 }} />
              <Tooltip title={t("print.tooltip")}>
                <IconButton
                  onClick={handlePrintTask}
                  size="small"
                  aria-label={t("print.tooltip")}
                  sx={{
                    color: "text.secondary",
                    opacity: 0.85,
                    "&:hover": { opacity: 1, color: "primary.main" },
                  }}
                >
                  <PrintRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton onClick={onClose} size="small" aria-label={t("closeCard")}>
                <CloseIcon />
              </IconButton>
            </Box>

            {inDoneColumn ? (
              <Box
                sx={{
                  px: 2.5,
                  py: 1.25,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  borderBottom: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                }}
              >
                <CheckCircleIcon sx={{ color: "success.main", fontSize: 22, mt: 0.15 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="success.dark">
                    {t("cardCompletedBanner")}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.15, lineHeight: 1.4 }}
                  >
                    {t("cardCompletedHint")}
                  </Typography>
                </Box>
              </Box>
            ) : null}

            {(() => {
              const scrollContent = (
                <Box sx={{ flex: 1, overflow: "auto", px: { xs: 2, sm: 3 }, py: 2.5 }}>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ minWidth: 0 }}>
                <Typography
                  component="span"
                  sx={{
                    flexShrink: 0,
                    fontSize: { xs: "1.05rem", sm: "1.2rem" },
                    fontWeight: 800,
                    lineHeight: 1.3,
                    color: "text.secondary",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  #{card.id}
                </Typography>
              <TextField
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("cardTitlePlaceholder")}
                fullWidth
                disabled={!canEdit}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: { xs: "1.25rem", sm: "1.5rem" },
                    fontWeight: 800,
                    lineHeight: 1.3,
                    color: inDoneColumn ? "text.secondary" : "text.primary",
                    textDecoration: inDoneColumn ? "line-through" : "none",
                    textDecorationColor: alpha(theme.palette.text.secondary, 0.45),
                  },
                }}
              />
              </Stack>

              {(card.stageTime || []).length > 0 ? (
                <TaskStageTimeBar
                  stageTime={card.stageTime}
                  totalSec={card.stageTimeTotalSec}
                  variant="detail"
                />
              ) : null}

              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mt: 2, mb: 2.5 }}>
                <Chip
                  icon={<AddIcon />}
                  label={t("cardAddAction")}
                  size="small"
                  clickable
                  onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                  sx={{ fontWeight: 600, borderRadius: 2, height: 32 }}
                />
                {(canManage || canExecute) && (
                  <QuickPill
                    icon={<PersonOutlineIcon />}
                    label={
                      (() => {
                        const total =
                          assignees.length +
                          clientAssignees.length +
                          (assignedToWorkspace ? 1 : 0);
                        return total
                          ? t("assigneeCount", { count: total })
                          : t("cardAssignees");
                      })()
                    }
                    active={
                      assignees.length > 0 ||
                      clientAssignees.length > 0 ||
                      assignedToWorkspace
                    }
                    onClick={() => setShowAssignees((v) => !v)}
                  />
                )}
                {canEdit && (
                  <>
                    <QuickPill
                      icon={<EventOutlinedIcon />}
                      label={dueDisplay || t("cardDueDate")}
                      active={Boolean(dueAt)}
                      onClick={() => setShowDueField((v) => !v)}
                    />
                    <QuickPill
                      icon={<AccessTimeOutlinedIcon />}
                      label={estimatedDuration || t("cardEstimatedDuration")}
                      active={Boolean(estimatedDuration)}
                      onClick={() => setShowDurationField((v) => !v)}
                    />
                    <QuickPill
                      icon={<FlagOutlinedIcon />}
                      label={t("cardRequiredShort")}
                      active={isRequired}
                      onClick={() => setIsRequired((v) => !v)}
                    />
                    {boardLabels.length > 0 && (
                      <QuickPill
                        icon={<LabelOutlinedIcon />}
                        label={t("cardLabels")}
                        active={labelIds.length > 0}
                      />
                    )}
                  </>
                )}
                {checklistCount > 0 && (
                  <QuickPill
                    icon={<CheckBoxOutlinedIcon />}
                    label={t("checklistCount", { count: checklistCount })}
                    active
                  />
                )}
                {attachmentCount > 0 && (
                  <QuickPill
                    icon={<AttachFileOutlinedIcon />}
                    label={t("attachmentCount", { count: attachmentCount })}
                    active
                  />
                )}
                {commentCount > 0 && (
                  <QuickPill
                    icon={<ChatBubbleOutlineIcon />}
                    label={t("commentCount", { count: commentCount })}
                    active
                  />
                )}
                {hasLinks && (
                  <QuickPill
                    icon={<AccountTreeOutlinedIcon />}
                    label={t("linkedTasksSection")}
                    active
                  />
                )}
              </Stack>

              <Menu
                anchorEl={addMenuAnchor}
                open={Boolean(addMenuAnchor)}
                onClose={() => setAddMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{ paper: { sx: { minWidth: 260, borderRadius: 2 } } }}
              >
                <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: "divider" }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t("cardAddMenuTitle")}
                  </Typography>
                </Box>
                {canEdit && (
                  <>
                    <MenuItem onClick={handleAddChecklist} disabled={addingChecklist}>
                      <ListItemIcon>
                        {addingChecklist ? (
                          <CircularProgress size={18} />
                        ) : (
                          <CheckBoxOutlinedIcon fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={addingChecklist ? t("creatingChecklist") : t("cardAddChecklist")}
                        secondary={t("cardAddChecklistHint")}
                      />
                    </MenuItem>
                    <MenuItem onClick={handleAddAttachmentClick}>
                      <ListItemIcon>
                        <AttachFileOutlinedIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("cardAddAttachment")}
                        secondary={t("cardAddAttachmentHint")}
                      />
                    </MenuItem>
                  </>
                )}
                <MenuItem onClick={handleOpenLinkedDialog}>
                  <ListItemIcon>
                    <LinkOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("cardAddLinkedTask")}
                    secondary={t("cardAddLinkedTaskHint")}
                  />
                </MenuItem>
              </Menu>

              {(showDueField || dueAt) && canEdit && (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    type="datetime-local"
                    value={dueAt}
                    onChange={(e) => setDueAt(e.target.value)}
                    fullWidth
                    size="small"
                    label={t("cardDueDate")}
                    InputLabelProps={{ shrink: true }}
                    helperText={t("cardDueDateHint")}
                  />
                </Box>
              )}

              {(showDurationField || estimatedDuration) && canEdit && (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    type="time"
                    value={estimatedDuration}
                    onChange={(e) => setEstimatedDuration(e.target.value)}
                    fullWidth
                    size="small"
                    label={t("cardEstimatedDuration")}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 60 }}
                    helperText={t("cardEstimatedDurationHint")}
                  />
                </Box>
              )}

              {showAssignees && (canManage || canExecute) && (
                <Box sx={{ mb: 2 }}>
                  <Autocomplete
                    multiple
                    options={assigneeOptions}
                    getOptionLabel={(o) => o.nome || o.name || String(o.id)}
                    isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                    value={assignees}
                    onChange={(_, v) => setAssignees(v)}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={isClientBoard ? "Responsáveis (equipe)" : t("cardAssignees")}
                        placeholder={t("cardAssigneesPlaceholder")}
                        helperText={
                          isClientBoard
                            ? "No portal, usuários da equipe aparecem como a empresa (workspace)."
                            : boardMembers.length > 0
                              ? t("cardAssigneesBoardMembersHint")
                              : undefined
                        }
                      />
                    )}
                  />
                  {isClientBoard ? (
                    <Stack spacing={1.25} sx={{ mt: 1.25 }}>
                      <Autocomplete
                        multiple
                        options={linkedPfClients}
                        getOptionLabel={(o) => o.name || String(o.id)}
                        isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                        value={clientAssignees}
                        onChange={(_, v) => setClientAssignees(v)}
                        size="small"
                        disabled={!linkedPfClients.length}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Responsáveis (clientes PF)"
                            placeholder={
                              linkedPfClients.length
                                ? "Cadastros PF vinculados a este PJ"
                                : "Nenhum PF vinculado a este cliente"
                            }
                          />
                        )}
                      />
                      <Chip
                        clickable
                        color={assignedToWorkspace ? "primary" : "default"}
                        variant={assignedToWorkspace ? "filled" : "outlined"}
                        label={
                          assignedToWorkspace
                            ? "Empresa (workspace) atribuída"
                            : "Atribuir empresa (workspace)"
                        }
                        onClick={() => setAssignedToWorkspace((v) => !v)}
                        sx={{ alignSelf: "flex-start", fontWeight: 700 }}
                      />
                    </Stack>
                  ) : null}
                </Box>
              )}

              {boardLabels.length > 0 && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    {t("cardLabels")}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
                    {boardLabels.map((label) => {
                      const selected = labelIds.includes(label.id);
                      return (
                        <Chip
                          key={label.id}
                          label={label.name}
                          size="small"
                          clickable={canEdit}
                          onClick={() => toggleLabel(label.id)}
                          sx={{
                            fontWeight: 600,
                            bgcolor: selected ? label.color : alpha(label.color, 0.15),
                            color: selected ? "#fff" : "text.primary",
                            border: `1px solid ${alpha(label.color, 0.5)}`,
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {hasLinks && (
                <Box
                  sx={{
                    mb: 2.5,
                    p: 1.5,
                    borderRadius: 2.5,
                    border: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
                    bgcolor: alpha(theme.palette.action.hover, 0.02),
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mb: 1.25 }}
                  >
                    <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ minWidth: 0 }}>
                      <AccountTreeOutlinedIcon
                        sx={{ fontSize: 20, color: "text.secondary", mt: 0.15 }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                          {t("linkedTasksSection")}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.25, lineHeight: 1.4 }}
                        >
                          {t("linkedTasksSectionHint")}
                        </Typography>
                      </Box>
                    </Stack>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleOpenLinkedDialog}
                      sx={{ flexShrink: 0, fontWeight: 600, textTransform: "none", borderRadius: 2 }}
                    >
                      {t("cardAddLinkedTask")}
                    </Button>
                  </Stack>

                  {pendingLinkedReview ? (
                    <Box
                      sx={{
                        mb: 1.25,
                        px: 1.25,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.28)}`,
                      }}
                    >
                      <Typography variant="body2" fontWeight={700} color="warning.dark" sx={{ lineHeight: 1.35 }}>
                        {t("linkedCompletionPendingReviewBanner")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35 }}>
                        {t("linkedCompletionPendingReviewHint")}
                      </Typography>
                    </Box>
                  ) : null}

                  <Stack spacing={1}>
                    {parentLink ? (
                      <LinkedTaskRow
                        link={parentLink}
                        role="parent"
                        onOpen={handleOpenLinkedCard}
                        t={t}
                        theme={theme}
                      />
                    ) : null}

                    {childLinks.length > 0 ? (
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                          sx={{ display: "block", mb: 0.75, px: 0.25 }}
                        >
                          {t("linkedTaskChildrenHint")}
                        </Typography>
                        <Stack spacing={0.75}>
                          {childLinks.map((child) => (
                            <LinkedTaskRow
                              key={child.id}
                              link={child}
                              role="child"
                              onOpen={handleOpenLinkedCard}
                              t={t}
                              theme={theme}
                            />
                          ))}
                        </Stack>
                      </Box>
                    ) : null}
                  </Stack>
                </Box>
              )}

              <TaskCardChecklists
                checklists={card.checklists || []}
                canManage={canEdit}
                cardId={card.id}
                mutations={cardMutations}
              />

              <TaskCardAttachments
                ref={attachmentsRef}
                attachments={card.attachments || []}
                canManage={canEdit}
                boardId={boardId}
                cardId={card.id}
                mutations={cardMutations}
              />

              <Grid container spacing={3}>
                <Grid item xs={12} md={showCardSidebar ? 7 : 12}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <NotesOutlinedIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" fontWeight={700}>
                      {t("cardDescription")}
                    </Typography>
                  </Stack>
                  <TextField
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t("cardDescriptionPlaceholder")}
                    fullWidth
                    multiline
                    minRows={5}
                    disabled={!canEdit}
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: alpha(theme.palette.background.default, 0.6),
                      },
                    }}
                  />
                </Grid>

                {showCardSidebar && (
                  <Grid item xs={12} md={5}>
                    <Divider sx={{ display: { md: "none" }, mb: 2 }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                      {t("cardSidebarTitle")}
                    </Typography>
                    <Stack spacing={1.5}>
                      {leadId && (
                        <Button
                          variant="outlined"
                          startIcon={<OpenInNewIcon />}
                          onClick={() =>
                            navigate(
                              resolveServiceChatNavPath(chatPath(leadId), {
                                pathname: location.pathname,
                                search: location.search,
                              })
                            )
                          }
                          sx={{ justifyContent: "flex-start", borderRadius: 2 }}
                        >
                          {t("openLead", { id: leadId })}
                        </Button>
                      )}
                      {discussionSpaceId && (
                        <Button
                          variant="outlined"
                          startIcon={<ChatBubbleOutlineIcon />}
                          onClick={() => navigate(`/equipe/${discussionSpaceId}`)}
                          sx={{ justifyContent: "flex-start", borderRadius: 2 }}
                        >
                          {t("openDiscussion")}
                        </Button>
                      )}
                      {isRequired && (
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.warning.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                            {t("cardRequiredHint")}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2.5 }} />

              <TaskCardComments
                comments={enrichedComments}
                cardId={card.id}
                currentUserId={currentUserId}
                mutations={cardMutations}
                users={users}
                boardMembers={board?.members || []}
                board={board}
              />
                </Box>
              );

              if (!canEdit) return scrollContent;

              return (
                <FileDropHandler
                  onFileUpload={handleAttachmentDrop}
                  dropLabel={t("attachmentDropOverlay")}
                  excludeSelector={TASK_COMMENT_DROP_ZONE_SELECTOR}
                  sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
                >
                  {scrollContent}
                </FileDropHandler>
              );
            })()}

            {(canManage || canEdit) && (
              <Box
                sx={{
                  px: 2.5,
                  py: 1.75,
                  borderTop: 1,
                  borderColor: inDoneColumn
                    ? alpha(theme.palette.success.main, 0.22)
                    : "divider",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                  bgcolor: inDoneColumn
                    ? alpha(theme.palette.success.main, 0.04)
                    : "background.paper",
                }}
              >
                <Button
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setDeleteOpen(true)}
                  disabled={saving || deleting || movingColumn}
                  sx={{ fontWeight: 600 }}
                >
                  {t("deleteCard")}
                </Button>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {canMove && !inDoneColumn ? (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={
                        movingColumn ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <CheckCircleIcon />
                        )
                      }
                      onClick={handleQuickComplete}
                      disabled={saving || deleting || movingColumn}
                      sx={{ fontWeight: 700, borderRadius: 2 }}
                    >
                      {t("completeTaskAction")}
                    </Button>
                  ) : null}
                  {canMove && inDoneColumn ? (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={
                        movingColumn ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <ReplayOutlinedIcon />
                        )
                      }
                      onClick={handleQuickReopen}
                      disabled={saving || deleting || movingColumn}
                      sx={{ fontWeight: 700, borderRadius: 2 }}
                    >
                      {t("reopenTaskAction")}
                    </Button>
                  ) : null}
                  {hasChanges ? (
                    <>
                      <Button onClick={onClose} disabled={saving || deleting || movingColumn}>
                        {inDoneColumn ? t("closeCompletedCard") : t("cancel")}
                      </Button>
                      <Button
                        variant={inDoneColumn ? "outlined" : "contained"}
                        onClick={handleSave}
                        disabled={!title.trim() || saving || deleting || movingColumn}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ fontWeight: 700, minWidth: 110, borderRadius: 2 }}
                      >
                        {saving
                          ? t("saving")
                          : inDoneColumn
                            ? t("saveCompletedChanges")
                            : t("save")}
                      </Button>
                    </>
                  ) : null}
                </Stack>
              </Box>
            )}

            {!canEdit && canExecute && (
              <Box
                sx={{
                  px: 2.5,
                  py: 1.75,
                  borderTop: 1,
                  borderColor: inDoneColumn
                    ? alpha(theme.palette.success.main, 0.22)
                    : "divider",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 1,
                  flexWrap: "wrap",
                  bgcolor: inDoneColumn
                    ? alpha(theme.palette.success.main, 0.04)
                    : "background.paper",
                }}
              >
                {hasChanges ? (
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!title.trim() || saving || movingColumn}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ fontWeight: 700, minWidth: 110, borderRadius: 2 }}
                  >
                    {saving ? t("saving") : t("save")}
                  </Button>
                ) : null}
                {!inDoneColumn ? (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={
                      movingColumn ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <CheckCircleIcon />
                      )
                    }
                    onClick={handleQuickComplete}
                    disabled={movingColumn}
                    sx={{ fontWeight: 700, borderRadius: 2 }}
                  >
                    {t("completeTaskAction")}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<ReplayOutlinedIcon />}
                    onClick={handleQuickReopen}
                    disabled={movingColumn}
                    sx={{ fontWeight: 700, borderRadius: 2 }}
                  >
                    {t("reopenTaskAction")}
                  </Button>
                )}
                <Button onClick={onClose} disabled={movingColumn}>
                  {inDoneColumn ? t("closeCompletedCard") : t("closeCard")}
                </Button>
              </Box>
            )}
          </Paper>
        </Fade>
      </Modal>

      <CreateLinkedTaskDialog
        open={linkedDialogOpen}
        onClose={() => !creatingLinked && setLinkedDialogOpen(false)}
        sourceBoard={board}
        onSubmit={handleCreateLinkedTask}
        isSubmitting={creatingLinked}
      />

      <ConfirmDialog
        open={deleteOpen}
        title={t("deleteCardTitle")}
        content={t("deleteCardBody")}
        confirmText={t("deleteCardConfirm")}
        cancelText={t("cancel")}
        onClose={() => !deleting && setDeleteOpen(false)}
        onConfirm={handleDelete}
        loading={deleting}
      />
    </>
  );
};

export default TaskCardModal;
